import { useCallback, useEffect, useState } from "react";
import { FileText, Loader2, Lock, ThumbsUp } from "lucide-react";

/* ------------------------------------------------------------------
   Deterministic designed cover. Derived only from the slug/sector text
   already present on the record: no logos, product imagery, or facts
   are invented. The cover is typographic + geometric, clearly a
   Favcy Select editorial treatment, never a company brand mark.
   ------------------------------------------------------------------ */
const PALETTES = [
  { a: "#C94A00", b: "#F2C9A0", c: "#2B211A" },
  { a: "#1F4E5F", b: "#A9CFCB", c: "#10222A" },
  { a: "#6B3A5B", b: "#E3BFD2", c: "#24131F" },
  { a: "#3F5A2A", b: "#CFDDB0", c: "#182112" },
  { a: "#23386B", b: "#B7C6EA", c: "#0F1830" },
  { a: "#8A5A12", b: "#EDD69C", c: "#2A1C06" },
];

function hash(text: string) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

export function ResearchCover({
  seed, companyName, sector, size = "card", className = "",
}: { seed: string; companyName: string; sector: string | null; size?: "card" | "hero"; className?: string }) {
  const h = hash(`${seed}|${sector ?? ""}`);
  const p = PALETTES[h % PALETTES.length];
  const variant = (h >> 4) % 3;
  const rot = (h >> 8) % 360;
  const circles = Array.from({ length: 5 }, (_, i) => ({
    cx: 40 + ((h >> (i * 3)) % 240), cy: 30 + ((h >> (i * 2 + 5)) % 120), r: 18 + ((h >> (i + 7)) % 60),
  }));
  return (
    <div
      className={`rl-cover relative isolate overflow-hidden ${size === "hero" ? "aspect-[16/10] md:aspect-auto md:min-h-[320px]" : "aspect-[16/9]"} ${className}`}
      style={{ backgroundColor: p.c }}
      aria-hidden="true"
      data-testid="img-research-cover"
    >
      <svg viewBox="0 0 320 180" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        <defs>
          <linearGradient id={`g${h}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={p.a} />
            <stop offset="1" stopColor={p.c} />
          </linearGradient>
        </defs>
        <rect width="320" height="180" fill={`url(#g${h})`} />
        {variant === 0 && circles.map((c, i) => (
          <circle key={i} cx={c.cx} cy={c.cy} r={c.r} fill="none" stroke={p.b} strokeOpacity={0.35 + i * 0.08} strokeWidth={1.2} />
        ))}
        {variant === 1 && (
          <g transform={`rotate(${rot % 40 - 20} 160 90)`}>
            {Array.from({ length: 14 }, (_, i) => (
              <rect key={i} x={-40 + i * 28} y={-40} width={10 + ((h >> i) % 12)} height={260} fill={p.b} fillOpacity={0.08 + (i % 4) * 0.05} />
            ))}
          </g>
        )}
        {variant === 2 && (
          <g>
            {Array.from({ length: 9 }, (_, r) => Array.from({ length: 16 }, (_, c) => (
              <circle key={`${r}-${c}`} cx={10 + c * 20} cy={10 + r * 20} r={((h >> ((r + c) % 20)) % 4) + 0.8} fill={p.b} fillOpacity={0.45} />
            )))}
          </g>
        )}
        <circle cx={260} cy={150} r={70} fill={p.b} fillOpacity={0.14} />
      </svg>
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4 md:p-5">
        <p
          className={`min-w-0 break-words font-serif leading-[1.02] ${size === "hero" ? "text-3xl md:text-5xl" : "text-xl"}`}
          style={{ color: "#FBF8F1" }}
        >
          {companyName}
        </p>
      </div>
      <p className="absolute left-4 top-4 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] md:left-5 md:top-5"
         style={{ backgroundColor: "rgba(12,10,8,.55)", color: "#FBF8F1" }}>
        {sector || "Research note"}
      </p>
    </div>
  );
}

/* What a preliminary evaluation is, and what is inside. */
export function ResearchExplainer({ compact = false }: { compact?: boolean }) {
  const inside = [
    ["Claims", "What the company says about itself, attributed as its own representations."],
    ["Evidence", "What our team has actually reviewed, with PIM page references where available."],
    ["Gaps", "What remains unproven or unverified."],
    ["Risks", "What could break the case."],
    ["PDF", "The full watermarked memorandum in the private Reading Room."],
  ];
  return (
    <section aria-labelledby="rl-explainer-title" className={`rl-panel rounded-2xl border ${compact ? "p-5" : "p-6 md:p-7"}`} data-testid="section-research-explainer">
      <p className="rl-accent font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">How to read this</p>
      <h2 id="rl-explainer-title" className="rl-ink mt-2 font-serif text-2xl">A preliminary evaluation, not a verdict.</h2>
      <p className="rl-body mt-3 max-w-[64ch] text-sm leading-6">
        Each note records a company we are studying. It is not an investment recommendation and does not mean the
        company has been admitted to any portfolio. Many companies under evaluation are never onboarded.
      </p>
      <dl className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-5">
        {inside.map(([term, desc]) => (
          <div key={term} className="rl-rule border-t pt-3">
            <dt className="rl-ink text-sm font-semibold">{term}</dt>
            <dd className="rl-muted mt-1 text-xs leading-5">{desc}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/* ------------------------------------------------------------------
   Private feedback, wired to /api/investor-portal/research/:pimId/feedback.
   Saved feedback for the current version is shown read-only.
   ------------------------------------------------------------------ */
export interface SavedResearchFeedback { useful: boolean | null; why: string | null; versionId: number; submittedAt: string }

const REASONS = ["Clear evidence", "Honest about gaps", "Useful risk framing", "Good sources", "Saved me time"];
const FEEDBACK_BASE = import.meta.env.BASE_URL;

export function composeWhy(reasons: string[], comment: string): string | null {
  const c = comment.trim();
  const parts: string[] = [];
  if (reasons.length) parts.push(`Reasons: ${reasons.join("; ")}`);
  if (c) parts.push(`Comment: ${c}`);
  return parts.length ? parts.join("\n") : null;
}

function feedbackHeaders(json = false): HeadersInit {
  return { "x-investor-token": localStorage.getItem("investor_token") || "", ...(json ? { "Content-Type": "application/json" } : {}) };
}

async function readError(r: Response, fallback: string) {
  try { const j = await r.json() as { error?: { message?: string } }; if (j.error?.message) return j.error.message; } catch { /* non-JSON */ }
  return fallback;
}

function FeedbackShell({ children }: { children: React.ReactNode }) {
  return (
    <section aria-labelledby="rl-feedback-title" className="rl-panel mt-12 max-w-[68ch] rounded-2xl border p-6" data-testid="section-research-feedback">
      <h2 id="rl-feedback-title" className="rl-ink font-serif text-2xl">Was this research useful?</h2>
      <p className="rl-muted mt-2 flex items-start gap-2 text-xs leading-5" data-testid="text-feedback-privacy">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Private. Your response is visible only to you and to Favcy Select administrators. It is never shown to the company or other investors.
      </p>
      {children}
    </section>
  );
}

export function ResearchFeedbackPanel({ pimId }: { pimId: string | number }) {
  const [saved, setSaved] = useState<SavedResearchFeedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [useful, setUseful] = useState(false);
  const [reasons, setReasons] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = `${FEEDBACK_BASE}api/investor-portal/research/${pimId}/feedback`;

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await fetch(url, { headers: feedbackHeaders(), credentials: "include", cache: "no-store" });
      if (!r.ok) throw new Error(await readError(r, "Your feedback status could not be loaded."));
      const j = await r.json() as { data: SavedResearchFeedback | null };
      setSaved(j.data ?? null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Your feedback status could not be loaded.");
    } finally { setLoading(false); }
  }, [url]);

  useEffect(() => { void load(); }, [load]);

  const why = composeWhy(reasons, comment);
  const canSend = (useful || !!comment.trim()) && (why?.length ?? 0) <= 2000 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true); setError(null);
    try {
      const r = await fetch(url, {
        method: "POST", headers: feedbackHeaders(true), credentials: "include",
        body: JSON.stringify({ useful: useful ? true : null, why }),
      });
      if (r.status === 409) {
        setError("Feedback for this version was already submitted and cannot be changed. Your saved response is shown below.");
        await load();
        return;
      }
      if (!r.ok) throw new Error(await readError(r, "Your feedback could not be sent. Please try again."));
      const j = await r.json() as { data: SavedResearchFeedback | null };
      if (j.data) setSaved(j.data); else await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Your feedback could not be sent. Please try again.");
    } finally { setSending(false); }
  };

  if (loading) {
    return <FeedbackShell><div role="status" data-testid="status-feedback-loading" className="mt-5 space-y-2"><div className="rl-track h-10 w-40 animate-pulse rounded-full" /><span className="sr-only">Loading feedback</span></div></FeedbackShell>;
  }
  if (loadError) {
    return (
      <FeedbackShell>
        <div role="alert" data-testid="status-feedback-load-error" className="research-error-surface mt-5 rounded border p-3 text-xs">
          <p>{loadError}</p>
          <button type="button" onClick={() => void load()} data-testid="button-retry-feedback" className="mt-2 font-semibold underline underline-offset-4">Try again</button>
        </div>
      </FeedbackShell>
    );
  }
  if (saved) {
    return (
      <FeedbackShell>
        {error && <p role="status" className="rl-body mt-4 text-xs" data-testid="status-feedback-conflict">{error}</p>}
        <div className="rl-rule mt-5 border-t pt-4" data-testid="status-feedback-sent">
          <p className="rl-ink text-sm font-semibold">
            {saved.useful ? <><ThumbsUp className="mr-2 inline h-4 w-4" aria-hidden="true" />You marked this research useful.</> : "You left a comment on this research."}
          </p>
          {saved.why && <p className="rl-body mt-2 whitespace-pre-line text-sm leading-6" data-testid="text-feedback-saved-why">{saved.why}</p>}
          <p className="rl-muted mt-3 font-mono text-[10px] uppercase tracking-wider">Submitted {new Date(saved.submittedAt).toLocaleString("en-IN")} · recorded for this version</p>
        </div>
      </FeedbackShell>
    );
  }
  return (
    <FeedbackShell>
      <button type="button" data-testid="button-feedback-useful" aria-pressed={useful} onClick={() => setUseful(v => !v)}
        className="rl-chip mt-5 inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-semibold">
        <ThumbsUp className="h-4 w-4" aria-hidden="true" /> Yes, useful
      </button>
      <div className="mt-5 space-y-4">
        {useful && (
          <fieldset>
            <legend className="rl-muted text-xs font-semibold">Why? (optional)</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {REASONS.map(r => (
                <button key={r} type="button" aria-pressed={reasons.includes(r)}
                  onClick={() => setReasons(list => list.includes(r) ? list.filter(x => x !== r) : [...list, r])}
                  data-testid={`button-feedback-reason-${r.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rl-chip min-h-9 rounded-full border px-3 text-xs">{r}</button>
              ))}
            </div>
          </fieldset>
        )}
        <div>
          <label htmlFor="rl-feedback-comment" className="rl-muted text-xs font-semibold">Comment {useful ? "(optional)" : "(or leave a comment on its own)"}</label>
          <textarea id="rl-feedback-comment" data-testid="input-feedback-comment" maxLength={1900} rows={3}
            value={comment} onChange={e => setComment(e.target.value)}
            className="rl-input mt-2 w-full rounded-lg border p-3 text-sm" />
        </div>
        {error && <p role="alert" className="research-error-surface rounded border p-3 text-xs" data-testid="status-feedback-error">{error}</p>}
        <button type="button" onClick={send} disabled={!canSend} data-testid="button-submit-feedback"
          className="rn-cta inline-flex min-h-11 items-center gap-2 rounded-md px-5 text-sm font-semibold">
          {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {sending ? "Sending" : "Send privately"}
        </button>
        <p className="rl-muted text-xs">Mark it useful, write a comment, or both. One response per version.</p>
      </div>
    </FeedbackShell>
  );
}

export function ResearchPdfNote() {
  return (
    <span className="rl-muted inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider">
      <FileText className="h-3.5 w-3.5" aria-hidden="true" /> PDF in Reading Room
    </span>
  );
}
